import { useState, useEffect, useCallback } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import BackToTop from "@/components/base/BackToTop";
import { adminApi } from "@/services/api";

interface AuditLog {
  id: number;
  userId: number;
  userName: string;
  userAvatar?: string;
  action: string;
  module: string;
  target: string;
  details: string;
  ip: string;
  timestamp: string;
  severity: "info" | "warning" | "critical";
  status: "success" | "failed";
}
import { exportToCSV, exportToExcel } from "@/utils/export";

type SeverityFilter = "all" | "info" | "warning" | "critical";
type ModuleFilter = "all" | string;

const severityConfig = {
  info:     { color: "#3b82f6", bg: "#eff6ff", icon: "ri-information-line",    label: "Info"     },
  warning:  { color: "#f59e0b", bg: "#fffbeb", icon: "ri-alert-line",           label: "Warning"  },
  critical: { color: "#ef4444", bg: "#fef2f2", icon: "ri-alarm-warning-line",   label: "Critical" },
};

const statusConfig = {
  success: { color: "#10b981", bg: "#ecfdf5" },
  failed:  { color: "#ef4444", bg: "#fef2f2" },
};

// ── Build export rows from AuditLog array ─────────────────────────────────────
function buildExportRows(logs: AuditLog[]) {
  return logs.map((l) => ({
    "Timestamp":  new Date(l.timestamp).toLocaleString("en-IN"),
    "Action":     l.action,
    "Severity":   l.severity,
    "Status":     l.status,
    "User":       l.userName,
    "Module":     l.module,
    "Target":     l.target,
    "IP Address": l.ip,
    "Details":    l.details,
  }));
}

export default function AdminAuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [severity, setSeverity]   = useState<SeverityFilter>("all");
  const [module,   setModule]     = useState<ModuleFilter>("all");
  const [search,   setSearch]     = useState("");
  const [selected, setSelected]   = useState<AuditLog | null>(null);
  const [loading,  setLoading]    = useState(false);
  const [page,     setPage]       = useState(1);
  const [hasMore,  setHasMore]    = useState(true);

  // Flush dialog
  const [flushConfirm, setFlushConfirm] = useState(false);
  const [flushing,     setFlushing]     = useState(false);

  // Export menu
  const [exportMenu,  setExportMenu]  = useState(false);
  const [exporting,   setExporting]   = useState(false);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" | "info" } | null>(null);

  // ── Map DB row → AuditLog ─────────────────────────────────────────────────
  const mapDbLog = (l: Record<string, unknown>): AuditLog => ({
    id:        (l.id        as number) || 0,
    userId:    (l.user_id   as string) || (l.userId   as string) || "",
    userName:  (l.user_name as string) || (l.userName as string) || "System",
    action:    (l.action    as string) || "",
    module:    (l.module    as string) || "System",
    target:    (l.target    as string) || "",
    details:   (l.details   as string) || "",
    ip:        (l.ip_address as string) || (l.ip as string) || "—",
    severity:  ((l.severity as string) || "info") as AuditLog["severity"],
    status:    ((l.status   as string) || "success") as AuditLog["status"],
    timestamp: (l.created_at as string) || (l.timestamp as string) || new Date().toISOString(),
  });

  const fetchLogs = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const data = await adminApi.getAuditLogs(pageNum, 50);
      const raw = data?.data || data?.logs || data || [];
      if (Array.isArray(raw)) {
        const mapped = raw.map(mapDbLog);
        if (pageNum === 1) setLogs(mapped);
        else setLogs((prev) => [...prev, ...mapped]);
        setHasMore(raw.length === 50);
      } else {
        setHasMore(false);
      }
    } catch {
      /* keep mock */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  const modules = Array.from(new Set(logs.map((l) => l.module)));

  const filtered = logs.filter((l) => {
    if (severity !== "all" && l.severity !== severity) return false;
    if (module   !== "all" && l.module   !== module)   return false;
    if (search && !l.action.toLowerCase().includes(search.toLowerCase()) &&
      !l.userName.toLowerCase().includes(search.toLowerCase()) &&
      !l.details.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchLogs(next);
  };

  const showToast = (msg: string, type: "success" | "error" | "info" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Flush ─────────────────────────────────────────────────────────────────
  const handleFlush = async () => {
    setFlushing(true);
    try {
      await adminApi.flushAuditLogs();
      setLogs([]);
      setSelected(null);
      setPage(1);
      setHasMore(false);
      setFlushConfirm(false);
      showToast("All audit logs cleared successfully");
    } catch {
      showToast("Failed to flush logs — check server connection", "error");
    } finally {
      setFlushing(false);
    }
  };

  // ── Export helpers ────────────────────────────────────────────────────────
  const exportFilename = `audit-logs-${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = () => {
    exportToCSV(buildExportRows(filtered), exportFilename);
    setExportMenu(false);
    showToast(`Exported ${filtered.length} log(s) to CSV`, "info");
  };

  const handleExportExcel = async () => {
    setExporting(true);
    await exportToExcel(buildExportRows(filtered), exportFilename, "Audit Logs");
    setExporting(false);
    setExportMenu(false);
    showToast(`Exported ${filtered.length} log(s) to Excel`, "info");
  };

  const handleExportPDF = () => {
    setExportMenu(false);
    // Build a clean print-friendly HTML table and open in new tab
    const rows = buildExportRows(filtered);
    if (!rows.length) { showToast("No logs to export", "error"); return; }
    const headers = Object.keys(rows[0]);
    const now = new Date().toLocaleString("en-IN");
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Audit Logs Export</title>
<style>
  body { font-family: system-ui, sans-serif; padding: 24px; color: #111; }
  h2  { margin-bottom: 4px; font-size: 18px; }
  p   { color: #666; font-size: 12px; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th  { background: #f3f4f6; text-align: left; padding: 6px 8px; border: 1px solid #e5e7eb; font-weight: 600; }
  td  { padding: 5px 8px; border: 1px solid #e5e7eb; vertical-align: top; }
  tr:nth-child(even) { background: #f9fafb; }
  .info     { color: #3b82f6; } .warning { color: #f59e0b; }
  .critical { color: #ef4444; } .success  { color: #10b981; }
  .failed   { color: #ef4444; }
  @media print { body { padding: 0; } }
</style></head><body>
<h2>Audit Logs Archive</h2>
<p>Exported ${rows.length} events &nbsp;·&nbsp; ${now}</p>
<table>
  <thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
  <tbody>
    ${rows.map((r) => `<tr>${headers.map((h) => {
      const v = String(r[h as keyof typeof r] ?? "");
      const cls = ["info","warning","critical","success","failed"].find((c) => v.toLowerCase() === c) || "";
      return `<td${cls ? ` class="${cls}"` : ""}>${v}</td>`;
    }).join("")}</tr>`).join("")}
  </tbody>
</table>
<script>window.onload=()=>{setTimeout(()=>{window.print();},300);}<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { showToast("Allow popups to export PDF", "error"); return; }
    w.document.write(html);
    w.document.close();
    showToast(`PDF print dialog opened for ${filtered.length} log(s)`, "info");
  };

  return (
    <ERPLayout title="Audit Logs" subtitle="Complete trail of all system actions and security events">
      <BackToTop scrollContainerId="erp-main-scroll" />

      {/* Toast */}
      {toast && (
        <div
          className="fixed top-4 right-4 z-50 text-white text-sm px-4 py-2.5 rounded-xl font-medium flex items-center gap-2"
          style={{ background: toast.type === "success" ? "#7c3aed" : toast.type === "info" ? "#3b82f6" : "#ef4444" }}
        >
          <i className={toast.type === "success" ? "ri-checkbox-circle-line" : toast.type === "info" ? "ri-information-line" : "ri-error-warning-line"} />
          {toast.msg}
        </div>
      )}

      {/* Flush Confirm Dialog */}
      {flushConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <i className="ri-delete-bin-2-line text-2xl text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Flush All Audit Logs?</h3>
            <p className="text-sm text-gray-500 mb-2">
              This will permanently delete <strong>all {logs.length} log entries</strong> from the database. This action cannot be undone.
            </p>
            <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg mb-5">
              <i className="ri-lightbulb-line mr-1" />Tip: Export a CSV/Excel archive first using the Export button before flushing.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setFlushConfirm(false)}
                className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
                Cancel
              </button>
              <button onClick={handleFlush} disabled={flushing}
                className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-bold cursor-pointer hover:bg-red-600 transition-all whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2">
                {flushing
                  ? <><i className="ri-loader-4-line animate-spin" />Flushing...</>
                  : <><i className="ri-delete-bin-2-line" />Yes, Flush All</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Events", value: logs.length,                                         color: "#7c3aed", bg: "#f5f3ff" },
            { label: "Info",         value: logs.filter((l) => l.severity === "info").length,     color: "#3b82f6", bg: "#eff6ff" },
            { label: "Warnings",     value: logs.filter((l) => l.severity === "warning").length,  color: "#f59e0b", bg: "#fffbeb" },
            { label: "Critical",     value: logs.filter((l) => l.severity === "critical").length, color: "#ef4444", bg: "#fef2f2" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-sm text-gray-600">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Log List */}
          <div className="lg:col-span-2 space-y-4">

            {/* Filters + action bar */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-48">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400 text-sm" />
                </span>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search actions, users, details..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400" />
              </div>

              {/* Severity */}
              <select value={severity} onChange={(e) => setSeverity(e.target.value as SeverityFilter)}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none cursor-pointer">
                <option value="all">All Severity</option>
                {["info","warning","critical"].map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>

              {/* Module */}
              <select value={module} onChange={(e) => setModule(e.target.value)}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none cursor-pointer">
                <option value="all">All Modules</option>
                {modules.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>

              {/* Refresh */}
              <button onClick={() => { setPage(1); fetchLogs(1); }}
                className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
                </span>
                Refresh
              </button>

              {/* Export dropdown */}
              <div className="relative">
                <button
                  onClick={() => setExportMenu((v) => !v)}
                  disabled={exporting || filtered.length === 0}
                  className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={exporting ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} />
                  </span>
                  Export
                  <i className="ri-arrow-down-s-line text-xs" />
                </button>
                {exportMenu && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-xl z-20 overflow-hidden">
                    <button onClick={handleExportCSV}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-file-text-line text-amber-600" />
                      </span>
                      Export CSV
                    </button>
                    <button onClick={handleExportExcel}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-file-excel-line text-amber-600" />
                      </span>
                      Export Excel
                    </button>
                    <button onClick={handleExportPDF}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-file-pdf-line text-red-500" />
                      </span>
                      Export PDF
                    </button>
                  </div>
                )}
              </div>

              {/* Flush */}
              <button onClick={() => setFlushConfirm(true)}
                className="flex items-center gap-1.5 border border-red-200 text-red-500 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer hover:bg-red-50 transition-all whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-delete-bin-2-line text-sm" />
                </span>
                Flush Logs
              </button>
            </div>

            {/* Count + export hint */}
            {filtered.length > 0 && (
              <div className="flex items-center justify-between px-1">
                <span className="text-xs text-gray-500">
                  Showing <strong className="text-gray-700">{filtered.length}</strong> of <strong className="text-gray-700">{logs.length}</strong> events
                  {(severity !== "all" || module !== "all" || search) && " (filtered)"}
                </span>
                <button
                  onClick={handleExportCSV}
                  className="text-xs text-violet-600 hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-download-line text-xs" />
                  Quick CSV export
                </button>
              </div>
            )}

            {/* Log entries */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">{filtered.length} event(s)</span>
                {filtered.length > 0 && (
                  <span className="text-xs text-gray-400">Click a row for details</span>
                )}
              </div>
              <div className="divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
                {filtered.map((log) => {
                  const sev = severityConfig[log.severity] || severityConfig.info;
                  const st  = statusConfig[log.status]   || statusConfig.success;
                  return (
                    <div
                      key={log.id}
                      onClick={() => setSelected(log)}
                      className={`flex items-start gap-4 px-5 py-4 cursor-pointer transition-all hover:bg-gray-50 ${selected?.id === log.id ? "bg-violet-50" : ""}`}
                    >
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: sev.bg }}>
                        <i className={`${sev.icon} text-sm`} style={{ color: sev.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800">{log.action}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ color: sev.color, background: sev.bg }}>{sev.label}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ color: st.color,  background: st.bg  }}>{log.status}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{log.details}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-gray-400">{log.userName}</span>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-gray-400">{log.module}</span>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-gray-400">{log.ip}</span>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-gray-400">
                            {new Date(log.timestamp).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <i className="ri-file-search-line text-4xl block mb-2" />
                    <p className="text-sm">No logs match your filters</p>
                  </div>
                )}
              </div>
              {hasMore && (
                <div className="px-5 py-3 border-t border-gray-100">
                  <button onClick={loadMore} disabled={loading}
                    className="w-full text-center text-xs font-semibold cursor-pointer hover:underline disabled:opacity-50 whitespace-nowrap"
                    style={{ color: "#7c3aed" }}>
                    {loading ? "Loading..." : "Load more logs"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selected ? (
              <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm">Event Detail</h3>
                  <div className="flex items-center gap-1.5">
                    {/* Export this single entry */}
                    <button
                      onClick={() => { exportToCSV(buildExportRows([selected]), `audit-log-${selected.id}`); showToast("Entry exported to CSV", "info"); }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                      title="Export this entry to CSV"
                    >
                      <i className="ri-download-line text-gray-400 text-sm" />
                    </button>
                    <button onClick={() => setSelected(null)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                      <i className="ri-close-line text-gray-400 text-sm" />
                    </button>
                  </div>
                </div>
                <div className="p-3 rounded-xl" style={{ background: severityConfig[selected.severity]?.bg || "#eff6ff" }}>
                  <p className="text-sm font-bold" style={{ color: severityConfig[selected.severity]?.color || "#3b82f6" }}>{selected.action}</p>
                  <p className="text-xs mt-0.5"   style={{ color: severityConfig[selected.severity]?.color || "#3b82f6" }}>
                    {severityConfig[selected.severity]?.label || "Info"} · {selected.status}
                  </p>
                </div>
                {[
                  { label: "User",      value: selected.userName },
                  { label: "Module",    value: selected.module   },
                  { label: "Target",    value: selected.target   },
                  { label: "IP Address",value: selected.ip       },
                  { label: "Timestamp", value: new Date(selected.timestamp).toLocaleString("en-IN", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{item.label}</p>
                    <p className="text-sm text-gray-800 mt-0.5">{item.value}</p>
                  </div>
                ))}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</p>
                  <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">{selected.details}</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
                <div className="text-center py-6 text-gray-400">
                  <i className="ri-file-search-line text-4xl block mb-2" />
                  <p className="text-sm">Click any log entry to view details</p>
                </div>
                {/* Quick export panel */}
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-600 mb-3">Archive &amp; Export</p>
                  <div className="space-y-2">
                    <button onClick={handleExportCSV} disabled={filtered.length === 0}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all disabled:opacity-50 whitespace-nowrap">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-file-text-line text-amber-600" />
                      </span>
                      <div className="text-left">
                        <p className="font-medium text-xs">Export CSV</p>
                        <p className="text-xs text-gray-400">{filtered.length} events</p>
                      </div>
                    </button>
                    <button onClick={handleExportExcel} disabled={exporting || filtered.length === 0}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all disabled:opacity-50 whitespace-nowrap">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-file-excel-line text-amber-600" />
                      </span>
                      <div className="text-left">
                        <p className="font-medium text-xs">Export Excel</p>
                        <p className="text-xs text-gray-400">{filtered.length} events</p>
                      </div>
                    </button>
                    <button onClick={handleExportPDF} disabled={filtered.length === 0}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all disabled:opacity-50 whitespace-nowrap">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-file-pdf-line text-red-500" />
                      </span>
                      <div className="text-left">
                        <p className="font-medium text-xs">Export PDF</p>
                        <p className="text-xs text-gray-400">Print-ready table</p>
                      </div>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-3">
                    <i className="ri-lightbulb-line mr-1" />Export an archive before flushing logs.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
